// BHP-1083: domains overview on Backend2.0 (Hydra `GET /api/domains`), Tailwind.
// Replaces the legacy activesubscription fan-out + react-bootstrap Cards. The
// detail route has no id, so the selected domain is handed off via the Zustand
// selection store (+ localStorage uuid), which the detail and DNS pages read.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { DomainRegistrationStatus } from '../../features/domain/domain';
import { useDomains } from '../../features/domain/application/domainQueries';
import { PAGE_SIZE } from '../../shared/api/useHydra';
import { toUserMessage } from '../../shared/http/errorMessage';

/**
 * Every status the backend enum can produce needs an entry — an unknown status
 * must still render a readable badge rather than `class="… undefined"`.
 */
const STATUS_STYLES: Record<DomainRegistrationStatus, string> = {
  registered: 'bg-teal-50 text-teal-700',
  registering: 'bg-amber-50 text-amber-700',
  pending: 'bg-slate-100 text-slate-600',
  failed: 'bg-rose-50 text-rose-700',
  cancelled: 'bg-slate-100 text-slate-500 line-through'
};
const UNKNOWN_STATUS_STYLE = 'bg-slate-100 text-slate-600';

function StatusBadge({
  status
}: {
  status: DomainRegistrationStatus;
}): JSX.Element {
  const style = STATUS_STYLES[status] ?? UNKNOWN_STATUS_STYLE;
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${style}`}>
      {status}
    </span>
  );
}

export default function DomainOverview(): JSX.Element {
  const [page, setPage] = useState(1);
  const { data, isPending, isError, error, isFetching } = useDomains(page);

  const domains = data?.items ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">My domains</h2>
        <Link
          to="/domain/check"
          className="rounded-md border-0 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white no-underline hover:bg-teal-700"
        >
          Check / register a domain
        </Link>
      </div>

      {isPending ? (
        <p className="text-sm text-slate-500">Loading domains…</p>
      ) : isError ? (
        <div
          role="alert"
          className="rounded-lg border border-solid border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
        >
          {toUserMessage(error, 'Could not load domains. Please try again.')}
        </div>
      ) : domains.length === 0 ? (
        <div className="rounded-xl border border-solid border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No domains for this customer.
        </div>
      ) : (
        <>
          <ul className="overflow-hidden rounded-xl border border-solid border-slate-200 bg-white">
            {domains.map((domain) => (
              <li
                key={domain.uuid}
                className="flex items-center justify-between gap-3 border-0 border-b border-solid border-slate-100 px-4 py-3 last:border-b-0"
              >
                <span className="flex items-center gap-3">
                  <span className="font-medium text-slate-800">
                    {domain.domainName}
                    {domain.tld !== null ? `.${domain.tld}` : ''}
                  </span>
                  <StatusBadge status={domain.registrationStatus} />
                </span>
                <Link
                  to={`/domain/details/${domain.uuid}`}
                  className="rounded-md border-0 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white no-underline hover:bg-teal-700"
                >
                  Details
                </Link>
              </li>
            ))}
          </ul>

          {lastPage > 1 ? (
            <nav
              aria-label="Domain pages"
              className="mt-4 flex items-center justify-between gap-3 text-sm"
            >
              <button
                type="button"
                disabled={page <= 1 || isFetching}
                onClick={() => {
                  setPage((current) => Math.max(1, current - 1));
                }}
                className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-slate-500">
                Page {page} of {lastPage} ({total} domains)
              </span>
              <button
                type="button"
                disabled={page >= lastPage || isFetching}
                onClick={() => {
                  setPage((current) => Math.min(lastPage, current + 1));
                }}
                className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          ) : null}
        </>
      )}
    </div>
  );
}
